import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  entitlementsFor,
  type Entitlements,
  type Locale,
  type PublicUser,
  type Tier,
} from 'shared';
import { api, ApiError, getSession, onSessionChange, setSession } from '@/lib/api';

export type PaidPlan = 'mystic' | 'oracle';

/** Narrows a query value (e.g. `?plan=`) to a plan we sell; anything else is ignored. */
export function asPlan(value: unknown): PaidPlan | null {
  return value === 'mystic' || value === 'oracle' ? value : null;
}

/**
 * Who the caller is and what they may do.
 *
 * `entitlements` always has a value — a signed-out visitor is the `guest`
 * tier, not an error state — so views can gate on it without null checks.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<PublicUser | null>(null);
  const tier = ref<Tier>('guest');
  const ready = ref(false);
  const busy = ref(false);
  const error = ref<string | null>(null);

  const entitlements = computed<Entitlements>(() => entitlementsFor(tier.value));
  const isSignedIn = computed(() => user.value !== null);
  const isPaid = computed(() => entitlements.value.paid);

  function reset(): void {
    user.value = null;
    tier.value = 'guest';
  }

  /** Reads the current session, if any. Safe to call on every app start. */
  async function hydrate(): Promise<void> {
    if (!getSession()) {
      reset();
      ready.value = true;
      return;
    }

    try {
      const data = await api<{ user: PublicUser }>('/me');
      user.value = data.user;
      tier.value = data.user.tier;
    } catch (cause) {
      // An expired or revoked session is an ordinary outcome, not an error to
      // show: the API client has already cleared it, so fall back to guest.
      if (cause instanceof ApiError && cause.isAuth) setSession(null);
      reset();
    } finally {
      ready.value = true;
    }
  }

  async function requestCode(email: string, locale: Locale, plan?: PaidPlan): Promise<void> {
    busy.value = true;
    error.value = null;
    try {
      await api('/auth/request-code', {
        method: 'POST',
        body: { email, locale, ...(plan ? { plan } : {}) },
        anonymous: true,
      });
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Could not send the code';
      throw cause;
    } finally {
      busy.value = false;
    }
  }

  async function verify(input: { email?: string; code?: string; linkToken?: string }): Promise<void> {
    busy.value = true;
    error.value = null;
    try {
      const data = await api<{
        accessToken: string;
        refreshToken: string;
        user: PublicUser;
      }>('/auth/verify', { method: 'POST', body: input, anonymous: true });

      setSession({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      user.value = data.user;
      tier.value = data.user.tier;
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Could not sign in';
      throw cause;
    } finally {
      busy.value = false;
    }
  }

  async function signOut(): Promise<void> {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // Revoking server-side is best effort; the local session goes either way.
    }
    setSession(null);
    reset();
  }

  /** Re-reads the user after a change made elsewhere, like a Stripe return. */
  async function refreshUser(): Promise<void> {
    if (!getSession()) return;
    try {
      const data = await api<{ user: PublicUser }>('/me');
      user.value = data.user;
      tier.value = data.user.tier;
    } catch {
      // Leave the last known state rather than flickering to guest on a blip.
    }
  }

  // The API client drops the session on its own when a refresh token dies.
  onSessionChange((session) => {
    if (!session) reset();
  });

  return {
    user, tier, ready, busy, error,
    entitlements, isSignedIn, isPaid,
    hydrate, requestCode, verify, signOut, refreshUser,
  };
});
